import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import useJwt from '@/auth/jwt/useJwt'
import { ref } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useCommon from "@/views/organization/useCommon"

export default function useWorkerEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { getItem, updateItem, createCognitoUser, updateCognitoEmail } = useLambda()
  const { assignSupervisorsBasedOnRoles, assignSupervisorsBasedOnDepartment } = useCommon()
  const worker = ref({})
  const isSubmitting = ref(false)
  const workerObjectId = oid(route.value.params.id)
  const query = { _id: workerObjectId }
  const collection = 'worker'
  const originalPrivileges = ref('')
  const originalEmail = ref('')

  const show = async () => {
    isSubmitting.value = true

    try {
      const item = await getItem({ collection, query })
      if (!item) throw new Error('Item not found')

      worker.value = {
        ...item,
        supervisors: item.supervisors?.map((e) => e.toString()) || [],
        locations: item.locations?.map((e) => e.toString()) || [],
        physical_locations: item.physical_locations?.map((e) => e.toString()) || [],
        roles: item.roles?.map((e) => e.toString()) || [],
      }

      originalPrivileges.value = item.privileges || ''
      originalEmail.value = item.email || ''
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.worker_fetch_error'))
    } finally {
      isSubmitting.value = false
    }
  }

  const update = async (data) => {
    isSubmitting.value = true

    try {
      const payload = {
        name: data.name?.value || '',
        email: data.email?.value || '',
        supervisors: data.supervisors?.value?.map(e => oid(e)) || [],
        locations: data.locations?.value?.map(e => oid(e)) || [],
        physical_locations: data.physical_locations?.value?.map(e => oid(e)) || [],
        roles: data.roles?.value?.map(e => oid(e)) || [],
        privileges: data.privileges?.value || '',
      }

      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // Update supervisors based on the selected roles
      const workerData = {
        _id: route.value.params.id,
        locations: data.locations?.value || [],
      }
      assignSupervisorsBasedOnRoles(data.roles?.value || [], [], workerData)

      // Update supervisors based on the selected departments
      assignSupervisorsBasedOnDepartment(data.locations?.value || [], [], route.value.params.id)

      // If privileges changed to consultant, director or supervisor, create Cognito user
      if (originalPrivileges.value === 'employee' && (payload.privileges === 'consultant' || payload.privileges === 'supervisor' || payload.privileges === 'director')) {
         try {
          const cognitoResult = await createCognitoUser({ email: data.email.value, name: data.name.value })

          if (cognitoResult.error) {
            throw new Error(`Cognito user creation failed: ${cognitoResult}`)
          }
          console.log("user created")
        } catch (error) {
          console.log(`Error creating user ${data.email.value}`)
          console.error(error)
          showErrorMessage(i18n.t('message.cognito_user_error'))
        }
      }

      // If user already has a Cognito account and email changed, update it
      const hasCognitoAccount = originalPrivileges.value === 'consultant' || originalPrivileges.value === 'supervisor' || originalPrivileges.value === 'director'
      const emailChanged = originalEmail.value && payload.email && payload.email !== originalEmail.value
      if (hasCognitoAccount && emailChanged) {
        try {
          await updateCognitoEmail({ currentEmail: originalEmail.value, newEmail: payload.email })
        } catch (error) {
          console.error(error)
          showErrorMessage(i18n.t('message.cognito_user_error'))
        }
      }


      showSuccessMessage(i18n.t('message.worker_updated'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.worker_update_error'))
    } finally {
      router.push({ name: 'organization-worker-list' })
      isSubmitting.value = false
    }
  }

  return {
    update,
    show,
    worker,
    isSubmitting,
  }
}
